import moment from 'moment';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Heading3, Paragraph, resizedAssetUrl, tokens } from '@treams/treams-ui';

import { GoodHabitzMark } from '../../../../../goals/GoodHabitzLogo';

const CardStyle = css<{ $elevated?: boolean; to?: string; $vertical?: boolean }>`
	border-radius: ${tokens.radius.md}px;
	display: flex;
	flex-direction: ${(props) => (props.$vertical ? 'row' : 'column')};
	transition:
		transform ease-out 250ms,
		box-shadow ease-out 250ms;
	position: relative;
	height: ${(props) => (props.$vertical ? 'auto' : '100%')};
	overflow: hidden;

	${(props) =>
		props.$elevated || props.to
			? css`
					box-shadow: ${tokens.shadow.xs};

					${props.to &&
					css`
						&:hover {
							transform: translateY(-3px);
							box-shadow: ${tokens.shadow.md};
						}
					`}
				`
			: css`
					border: 1px solid ${tokens.border.subtle};
				`}
`;

const CardLink = styled(Link)<{ $elevated?: boolean; $vertical?: boolean }>`
	${CardStyle}
`;

const CardElement = styled.div<{ $elevated?: boolean; $vertical?: boolean }>`
	${CardStyle}
`;

const CoverContainer = styled.div<{ $vertical?: boolean }>`
	background: ${tokens.surface.sidebar};
	overflow: hidden;
	flex-shrink: 0;
	position: relative;

	${(props) =>
		props.$vertical
			? css`
					width: 80px;
					min-height: 80px;
					align-self: stretch;
				`
			: css`
					width: 100%;
					height: 150px;
				`}
`;

const CoverContainerSkeleton = styled.div<{ $vertical?: boolean }>`
	background: ${tokens.surface.skeleton};
	overflow: hidden;
	flex-shrink: 0;

	${(props) =>
		props.$vertical
			? css`
					width: 80px;
					min-height: 80px;
					align-self: stretch;
				`
			: css`
					width: 100%;
					height: 150px;
				`}
`;

const ProviderMark = styled.div<{ $vertical?: boolean }>`
	position: absolute;
	left: ${(props) => (props.$vertical ? 6 : 8)}px;
	bottom: ${(props) => (props.$vertical ? 6 : 8)}px;
`;

const Cover = styled.img<{ $cancelled?: boolean }>`
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
	${(props) => (props.$cancelled ? 'opacity: 0.4;' : '')}
`;

const CardBox = styled(Box)`
	background: ${tokens.surface.white};
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-width: 0;
`;

const TypeDot = styled(Box)<{ $color: string }>`
	background: ${(props) => props.$color};
	width: 10px;
	height: 10px;
	border-radius: ${tokens.radius.full}px;
	flex-shrink: 0;
	flex-grow: 0;
`;

const CalendarIconMonth = styled.p`
	color: ${tokens.text.inverse};
	background-color: ${tokens.error.icon};
	border-radius: ${tokens.radius.xs}px ${tokens.radius.xs}px 0 0;
	display: flex;
	font-size: 10px;
	align-items: center;
	justify-content: center;
	text-transform: uppercase;
	font-weight: ${tokens.typography.weights.semibold};
	padding: 2px 5px 0 5px;
	line-height: 14px;
`;

const CalendarIconDay = styled.p`
	color: ${tokens.text.primary};
	background-color: ${tokens.surface.white};
	display: flex;
	font-size: ${tokens.typography.sizes.h3}px;
	align-items: center;
	justify-content: center;
	border-radius: 0 0 ${tokens.radius.xs}px ${tokens.radius.xs}px;
	padding: 2px 0;
`;

const CalendarIcon = ({ date }: { date: Date }) => (
	<Box width={36}>
		<CalendarIconMonth>{moment(date).format('MMM')}</CalendarIconMonth>
		<CalendarIconDay>{moment(date).date()}</CalendarIconDay>
	</Box>
);

const Title = styled(Heading3)<{ $cancelled?: boolean; $compact?: boolean }>`
	${(props) => (props.$cancelled ? 'text-decoration: line-through;' : '')}
	${(props) =>
		props.$compact &&
		css`
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
		`}
`;

const Pill = styled.div`
	background-color: ${tokens.error.icon};
	border-radius: ${tokens.radius.xs}px;
	color: ${tokens.text.inverse};
	padding: 0 ${tokens.padding.control}px;
	font-size: ${tokens.typography.sizes.small}px;
`;

type LearningItemCardProps = (
	| { event: graphql.LearningEvent }
	| { course: graphql.LearningCourseFragment }
) & {
	children?: ReactNode;
	vertical?: boolean;
};

const LearningItemCard = (props: LearningItemCardProps) => {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const location = useLocation();
	const vertical = Boolean(props.vertical);

	const isEvent = 'event' in props;
	const item = isEvent ? props.event : props.course;
	const cancelled = isEvent && props.event.status === graphql.LearningEventStatus.Cancelled;
	const imageUrl = isEvent
		? props.event.cover?.source.url &&
			resizedAssetUrl(props.event.cover.source.url, {
				width: vertical ? 160 : 300,
				height: vertical ? 160 : 150,
			})
		: props.course.imageUrl;
	const pathname = isEvent
		? `/learning/event/${props.event.id}`
		: `/learning/course/${props.course.id}`;

	const courseMeta = !isEvent
		? [
				localize(props.course.learningType.title).text,
				props.course.durationMinutes &&
					t('learning.library.course.duration', {
						minutes: props.course.durationMinutes,
					}),
			]
				.filter(Boolean)
				.join(' · ')
		: null;

	const body = (
		<>
			<CoverContainer $vertical={vertical}>
				{imageUrl && <Cover src={imageUrl} $cancelled={cancelled} />}
				{!isEvent && props.course.provider === graphql.LearningCourseProvider.Goodhabitz && (
					<ProviderMark $vertical={vertical}>
						<GoodHabitzMark alt="GoodHabitz" />
					</ProviderMark>
				)}
			</CoverContainer>

			{cancelled && (
				<Box style={{ position: 'absolute', top: 10, left: 10 }}>
					<Pill>{t('learning.event.cancelled')}</Pill>
				</Box>
			)}

			{isEvent && !vertical && (
				<Box style={{ position: 'absolute', top: 10, right: 10 }}>
					<CalendarIcon date={props.event.startDate} />
				</Box>
			)}

			<CardBox py={vertical ? 2 : 12} px={vertical ? 2 : 3}>
				{isEvent ? (
					<Flex alignItems="center">
						<Box flexGrow={1} flexShrink={1} minWidth={0}>
							<Title $cancelled={cancelled} $compact={vertical}>
								{localize(item.title)?.text}
							</Title>
						</Box>
						{!vertical && <TypeDot $color={item.learningType.color} style={{ marginTop: -3 }} />}
					</Flex>
				) : (
					<>
						<Title $compact={vertical}>{localize(item.title)?.text}</Title>
						<Flex alignItems="center" mt={1}>
							<TypeDot $color={item.learningType.color} mr={2} />
							<Paragraph mb={0} style={{ color: tokens.text.secondary }}>
								{courseMeta}
							</Paragraph>
						</Flex>
					</>
				)}
				{props.children && (
					<Box mt="auto" pt={vertical ? 1 : 3}>
						{props.children}
					</Box>
				)}
			</CardBox>
		</>
	);

	// Extra actions (e.g. an add button) cannot live inside the card link.
	if (props.children) {
		return (
			<CardElement $elevated $vertical={vertical}>
				{body}
			</CardElement>
		);
	}

	return (
		<CardLink
			$vertical={vertical}
			to={{
				pathname,
				state: {
					backgroundLocation: location,
				},
			}}
		>
			{body}
		</CardLink>
	);
};

LearningItemCard.Skeleton = function LearningItemCardSkeleton({
	vertical,
}: {
	vertical?: boolean;
}) {
	return (
		<CardElement $vertical={vertical}>
			<CoverContainerSkeleton $vertical={vertical} />

			<CardBox py={vertical ? 2 : 12} px={vertical ? 2 : 3}>
				<Flex alignItems="center">
					<Box flexGrow={1} flexShrink={1}>
						<Paragraph.Skeleton lines={1} />
					</Box>
				</Flex>
			</CardBox>
		</CardElement>
	);
};

export default LearningItemCard;
